import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";
import { Loader } from "semantic-ui-react";
import { AnsweredQuestionWithId } from "@teamprevent/common/model";
import { submitInvalidQuestionnaire as submitInvalidQuestionnaireAction } from "@teamprevent/common/questionnaires/actions";
import { navigateToParentRoute } from "@teamprevent/common/routing/actions";
import { HealthSurveillanceQuestionnaire, HealthSurveillanceQuestionnaireKey } from "@teamprevent/common/healthSurveillance";

import { ApplicationState } from "../../applicationState";
import { useOhCaseDetailsModel } from "../../crud/ohCase";
import { getHealthSurveillanceQuestionnaireEvidenceApi } from "../healthSurveillanceApi";
import {
    deleteEmployeeQuestionnaireEvidence, loadHealthSurveillanceQuestionnaire, submitNonClinicHealthSurveillanceQuestionnaire
} from "../actions";
import { orgStructureApi } from "../../crud/orgStructure";
import { useEmployeeRecordDetailsModel } from "../../crud/employeeRecord/hooks";
import { useCustomMedicalTypes } from "../../customMedicalTypes/hooks/useCustomMedicalTypes";
import { useMedicalTypesForOhCase } from "../hooks/useMedicalTypesForOhCase";

export const HealthSurveillanceEmployeeQuestionnaireForTask: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const customerId = useSelector((state: ApplicationState) => state.router.params!.customerId);
    const ohCaseId = useSelector((state: ApplicationState) => state.router.params!.ohCaseId);
    const taskId = useSelector((state: ApplicationState) => state.router.params!.taskId);
    const { ohCase, isLoaded: isOhCaseLoaded } = useOhCaseDetailsModel({ ohCaseId, customerId });
    const task = isOhCaseLoaded ? ohCase?.tasks?.find(t => t.id === taskId) : undefined;
    const submitQuestionnaire = React.useCallback(async (questions: AnsweredQuestionWithId[]): Promise<boolean> => {
        await dispatch(submitNonClinicHealthSurveillanceQuestionnaire(ohCaseId, taskId, questions));
        return true;
    }, [dispatch, ohCaseId, taskId]);
    const submitInvalidQuestionnaire = React.useCallback(() => dispatch(submitInvalidQuestionnaireAction(HealthSurveillanceQuestionnaireKey)), [dispatch]);
    const navigateToNextPage = React.useCallback(() => dispatch(navigateToParentRoute()), [dispatch]);
    const getOrgLevels = React.useCallback(async () =>
        (await orgStructureApi.getAll(customerId, undefined)).map(o => ({ id: o.id, name: o.name ?? "" })), [customerId]);
    const getQuestionnaire = React.useCallback(() => {
        if (task!.completed) {
            return dispatch(loadHealthSurveillanceQuestionnaire(ohCaseId, taskId, HealthSurveillanceQuestionnaireKey));
        }

        return Promise.resolve([]);
    }, [dispatch, ohCaseId, task, taskId]);
    const { employeeRecord } = useEmployeeRecordDetailsModel({ customerId, employeeRecordId: ohCase?.employeeRecordId ?? "" }, { enable: isOhCaseLoaded });
    const onDeleteEvidence = React.useCallback(
        (_fileId: string, _fileName: string, ohCaseDocumentId: string) => {
            return dispatch(deleteEmployeeQuestionnaireEvidence(ohCaseId, customerId, ohCaseDocumentId));
        },
        [customerId, dispatch, ohCaseId]);
    const {
        medicalTypes: medicalTypesForQuestionnaire,
        refetch: loadMedicalTypesForQuestionnaire
    } = useMedicalTypesForOhCase({ customerId, ohCaseId }, { enable: false });
    const {
        customMedicalTypes,
        refetch: loadCustomMedicalTypes
    } = useCustomMedicalTypes({ enable: false });

    if (isOhCaseLoaded === false) {
        return <Loader active />;
    }

    const fileUploadApi = getHealthSurveillanceQuestionnaireEvidenceApi(ohCaseId, customerId);

    return (<HealthSurveillanceQuestionnaire
        task={task}
        fileUploadApi={fileUploadApi}
        getDownloadLink={undefined}
        submitQuestionnaire={submitQuestionnaire}
        submitInvalidQuestionnaire={submitInvalidQuestionnaire}
        navigateToNextPage={navigateToNextPage}
        getOrgLevels={getOrgLevels}
        getQuestionnaire={getQuestionnaire}
        recordMedicalsSeparately={true}
        autoFill={true}
        employeeRecord={employeeRecord}
        onDeleteFile={onDeleteEvidence}
        loadMedicalTypesForQuestionnaire={loadMedicalTypesForQuestionnaire}
        loadCustomMedicalTypes={loadCustomMedicalTypes}
        medicalTypesForQuestionnaire={medicalTypesForQuestionnaire}
        customMedicalTypes={customMedicalTypes}
    />);
};
